/**
 * MGC-S360 · Estado de "dirty" por projeto (change-based, não time-based)
 *
 * Rastreia, POR PROJETO, dois timestamps em localStorage (bucket por workspace):
 *   • last_change_at    — última mutação relevante que afeta o motor (write-through
 *                         em atividade/projeto, movimentação de item MGC, etc.)
 *   • last_motor_run_at — última execução bem-sucedida do motor MGC S360
 *
 * O projeto é considerado "dirty" (precisa rerodar o motor + Val) quando:
 *   • never ran        → `last_motor_run_at` é null; OU
 *   • has changes      → `last_change_at > last_motor_run_at`
 *
 * A tela "Ações e Decisões" usa `getProjetoMotorState(id)` no mount para
 * decidir se dispara motor+Val automaticamente ou reutiliza a versão anterior.
 *
 * O botão "Atualizar Informações" força uma nova rodada mesmo se `isDirty=false`
 * (uso: quando o GP quer forçar sem uma mudança detectada — ex.: mudanças
 * externas ao contexto local que este helper não vê).
 */
const LS_BASE = "s360_motor_state_v1";
const EVENT = "s360-motor-state:updated";

function scope() {
  try {
    const s = JSON.parse(localStorage.getItem("app_session") || "null");
    return s?.workspace_id || "mock";
  } catch { return "mock"; }
}
function key() { return `${LS_BASE}__${scope()}`; }

function readAll() {
  try {
    const raw = typeof window !== "undefined" ? localStorage.getItem(key()) : null;
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}
function writeAll(map) {
  try {
    localStorage.setItem(key(), JSON.stringify(map));
    if (typeof window !== "undefined") window.dispatchEvent(new Event(EVENT));
  } catch { /* quota */ }
}

/**
 * Retorna o estado atual do motor para o projeto.
 * @returns {{ last_change_at: string|null, last_motor_run_at: string|null, isDirty: boolean }}
 */
export function getProjetoMotorState(projetoId) {
  if (!projetoId) return { last_change_at: null, last_motor_run_at: null, isDirty: true };
  const all = readAll();
  const st = all[projetoId] || {};
  const isDirty =
    !st.last_motor_run_at
    || (st.last_change_at && st.last_change_at > st.last_motor_run_at);
  return {
    last_change_at: st.last_change_at || null,
    last_motor_run_at: st.last_motor_run_at || null,
    isDirty: !!isDirty,
  };
}

/** Marca o projeto como "dirty" (mudança detectada AGORA). */
export function markProjetoDirty(projetoId) {
  if (!projetoId) return;
  const all = readAll();
  all[projetoId] = { ...(all[projetoId] || {}), last_change_at: new Date().toISOString() };
  writeAll(all);
}

/** Marca que o motor rodou com sucesso para o projeto. */
export function markProjetoMotorRan(projetoId, at = null) {
  if (!projetoId) return;
  const all = readAll();
  all[projetoId] = { ...(all[projetoId] || {}), last_motor_run_at: (at || new Date().toISOString()) };
  writeAll(all);
}

/** Inscreve callback para receber notificação quando o estado muda. */
export function subscribeMotorState(cb) {
  if (typeof window === "undefined") return () => {};
  const handler = () => cb();
  window.addEventListener(EVENT, handler);
  return () => window.removeEventListener(EVENT, handler);
}
